import { useEffect, useMemo, useRef, useState } from 'react';
import type { Account } from '../lib/accounts';
import { usd, type Order, type Quote, type IncomingPO } from '../lib/clientDeepData';
import type { WorkTask } from '../WorkflowContext';
import { canonicalClientId } from '../lib/dna';
import { osQuoteEngineUrl } from '../lib/osData';

export type PortalKind = 'orders' | 'quotes' | 'pos' | 'tasks';
const KINDS: [PortalKind, string][] = [['quotes', 'Quotes'], ['orders', 'Orders'], ['pos', 'Purchase Orders'], ['tasks', 'Requests']];

interface Props {
  kind: PortalKind; client?: Account;
  orders: Order[]; quotes: Quote[]; pos: IncomingPO[]; tasks: WorkTask[];
  focusId?: string;
  onClose: () => void;
}

type Decision = 'approved' | 'changes' | null;

export default function ClientPortal({ kind: initial, client, orders, quotes, pos, tasks, focusId, onClose }: Props) {
  const [kind, setKind] = useState<PortalKind>(initial);
  const [selId, setSelId] = useState<string | null>(focusId ?? null);
  const [files, setFiles] = useState<Record<string, string[]>>({});
  const [decision, setDecision] = useState<Record<string, Decision>>({});
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [drag, setDrag] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const items = useMemo(() => {
    if (kind === 'quotes') return quotes.map((q) => ({ id: q.id, title: q.id, sub: q.product, value: q.value, status: q.status, qty: q.qty, osId: q.osId as string | undefined }));
    if (kind === 'orders') return orders.map((o) => ({ id: o.id, title: o.id, sub: o.product, value: o.value, status: o.status.replace('_', ' '), qty: o.qty, osId: undefined as string | undefined }));
    if (kind === 'pos') return pos.map((p, i) => ({ id: p.po + i, title: p.po, sub: `Received ${p.received} · ${p.ship}`, value: p.value, status: p.status, qty: '', osId: undefined as string | undefined }));
    return tasks.map((t) => ({ id: t.id, title: t.title, sub: `${t.owner} · due ${t.due}`, value: 0, status: t.status, qty: '', osId: undefined as string | undefined }));
  }, [kind, quotes, orders, pos, tasks]);

  const sel = items.find((x) => x.id === selId) || items[0];
  const selKey = sel?.id ?? '';
  const myFiles = files[selKey] || [];
  const dec = decision[selKey] ?? null;

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const names = Array.from(list).map((f) => f.name);
    setFiles((m) => ({ ...m, [selKey]: [...(m[selKey] || []), ...names] }));
  };

  const clientName = client?.name ?? 'Client';
  const portalUrl = `portal.microflexfilm.com/${canonicalClientId(client).toLowerCase()}`;
  const isQuote = kind === 'quotes';
  const lineUnit = sel && sel.value ? Math.round((sel.value / 1000) * 100) / 100 : 0;

  return (
    <div className="portal-scrim" onClick={onClose}>
      <div className="portal" onClick={(e) => e.stopPropagation()}>
        {/* portal header — client-facing branding */}
        <div className="portal-head">
          <div className="portal-brand">
            <div className="portal-logo">M</div>
            <div>
              <div className="portal-name">Microflex Client Portal</div>
              <div className="portal-url">{portalUrl}</div>
            </div>
          </div>
          <div className="portal-preview-flag">Client preview · {clientName}</div>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="portal-tabs">
          {KINDS.map(([k, label]) => (
            <button key={k} className={`portal-tab${kind === k ? ' on' : ''}`} onClick={() => { setKind(k); setSelId(null); }}>{label}</button>
          ))}
        </div>

        <div className="portal-body">
          {/* item list */}
          <div className="portal-list">
            {items.length === 0 && <div className="empty" style={{ padding: 24 }}>Nothing here yet.</div>}
            {items.map((it) => (
              <button key={it.id} className={`portal-item${(sel?.id === it.id) ? ' on' : ''}`} onClick={() => setSelId(it.id)}>
                <div className="portal-item-main"><strong>{it.title}</strong><span>{it.sub}</span></div>
                {it.value ? <span className="portal-item-val">{usd(it.value)}</span> : null}
              </button>
            ))}
          </div>

          {/* item detail */}
          <div className="portal-detail">
            {!sel ? <div className="empty" style={{ padding: 40 }}>Select an item.</div> : (
              <>
                <div className="portal-detail-head">
                  <div>
                    <h3>{sel.title}</h3>
                    <div className="portal-sub">{sel.sub}{sel.qty ? ` · ${sel.qty}` : ''}</div>
                  </div>
                  <span className={`portal-status ${dec === 'approved' ? 'ok' : dec === 'changes' ? 'warn' : ''}`}>
                    {dec === 'approved' ? 'Approved by client' : dec === 'changes' ? 'Changes requested' : `Status: ${sel.status}`}
                  </span>
                </div>

                {sel.value ? (
                  <div className="portal-lines">
                    <div className="portal-line head"><span>Description</span><span>Qty</span><span>Unit</span><span>Total</span></div>
                    <div className="portal-line"><span>{sel.sub}</span><span>{sel.qty || '—'}</span><span>{usd(lineUnit)}</span><span>{usd(Math.round(sel.value * 0.9))}</span></div>
                    <div className="portal-line"><span>Freight &amp; handling</span><span>—</span><span>—</span><span>{usd(Math.round(sel.value * 0.1))}</span></div>
                    <div className="portal-line total"><span>Total</span><span /><span /><span>{usd(sel.value)}</span></div>
                  </div>
                ) : null}

                {/* edit */}
                <div className="portal-section">
                  <div className="section-h">Edit / Request</div>
                  <div className="form-grid">
                    <div className="field"><label>Adjust quantity</label><input placeholder={sel.qty || 'e.g. 25K pcs'} value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                    <div className="field"><label>Requested delivery</label><input placeholder="e.g. 2 weeks" /></div>
                    <div className="field full"><label>Notes to Microflex</label><textarea placeholder="Add SKU confirmations, artwork notes, or special instructions…" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                  </div>
                </div>

                {/* upload */}
                <div className="portal-section">
                  <div className="section-h">Upload {isQuote ? 'PO & Artwork' : 'Documents'}</div>
                  <div className={`upload-zone${drag ? ' drag' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
                    onClick={() => fileRef.current?.click()}>
                    <div className="upload-ico">⬆</div>
                    <div>Drag &amp; drop files here, or <span className="upload-link">browse</span></div>
                    <div className="upload-hint">PO, signed quote, artwork, dielines — PDF, AI, PNG, JPG</div>
                    <input ref={fileRef} type="file" multiple hidden onChange={(e) => addFiles(e.target.files)} />
                  </div>
                  {myFiles.length > 0 && (
                    <div className="file-chips">
                      {myFiles.map((f, i) => (
                        <span className="file-chip" key={i}>📎 {f}<button onClick={() => setFiles((m) => ({ ...m, [selKey]: m[selKey].filter((_, j) => j !== i) }))}>×</button></span>
                      ))}
                    </div>
                  )}
                </div>

                {/* actions */}
                <div className="portal-actions">
                  {isQuote && (sel as any).osId && <button className="btn primary" onClick={() => window.open(osQuoteEngineUrl({ quoteId: (sel as any).osId, customer: client?.name, customerId: (client?.id || '').replace(/^cust_/, '') }), '_blank', 'noreferrer')}>↗ Open in OS Quote Engine</button>}
                  {isQuote && <button className="btn primary" onClick={() => setDecision((d) => ({ ...d, [selKey]: 'approved' }))}>✓ Approve quote</button>}
                  <button className="btn" onClick={() => setDecision((d) => ({ ...d, [selKey]: 'changes' }))}>Request changes</button>
                  <button className="btn">Download PDF</button>
                  <button className="btn">Message account team</button>
                </div>
                {dec && <div className={`portal-banner ${dec === 'approved' ? 'ok' : 'warn'}`}>
                  {dec === 'approved'
                    ? `Quote ${sel.title} approved. Microflex has been notified and will convert it to a sales order.`
                    : `Change request sent for ${sel.title}. Your account team will follow up.`}
                </div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
